import React, { useEffect } from 'react'
import { Typography, Space, Card, Button, Avatar } from 'antd'
import qs from 'qs'
import { StepComponentProps, OnboardingV2Data } from '../index'
import IntlMessages from '../../../util/IntlMessages'
import { useHistory } from 'react-router-dom'

import { useOnboardingContext, useMarkStepCompleted } from '../context'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { track } from '../../../services/analytics/analytics'
import dayjs from 'dayjs'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'

const { Title, Paragraph, Text } = Typography

export const CompletionStep: React.FC<StepComponentProps<OnboardingV2Data>> = ({ onClose, isLoading }) => {
  const history = useHistory()
  const { formData: data } = useOnboardingContext()
  const markStepCompleted = useMarkStepCompleted()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)

  useEffect(() => {
    if (authCompany && data?.authUser) {
      track('ONBOARDING_STEP_5_FINAL_SHOWN', {
        company_id: authCompany.id,
        user_id: data.authUser.id,
      })
    }

    // Mark completion step as completed and mark onboarding as complete only if user has onboardingV2 URL parameter
    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
    const hasOnboardingV2Param = queryParams.onboardingV2 === 'true'
    if (hasOnboardingV2Param) {
      markStepCompleted('completion', true)
    }
  }, [authCompany, data?.authUser, markStepCompleted])

  // Get location data from authUser or onboarding data
  const locationName = data.locationName || data.authUser?.location?.name || 'Not set'
  const leavePolicies = data.authUser?.location?.leavePolicies || []
  // Prefer counting imported heatmap holidays; fallback to auth user's location holidays
  const holidaysCount = React.useMemo(() => {
    if (Array.isArray(data.holidays) && data.holidays.length > 0) {
      const currentYear = dayjs().year()
      return data.holidays?.filter((holiday) => dayjs(holiday.date)?.year() === currentYear)?.length
    }
    return 0
  }, [data.holidays])

  // Get department data
  const approvers = data.authUser?.team?.approvers || []
  const users = data.authUser?.id ? [{ id: data.authUser.id, name: data.authUser.name || 'You', imageUrl: data.authUser.imageUrl }] : []

  // Approvers include imageUrl via getCompanyAndUserInfo

  return (
    <div className="completion-step">
      <div className="step-content">
        <div className="step-left-section">
          <div>
            <div style={{ marginBottom: 40 }}>
              <Title level={2} style={{ fontSize: 32, marginBottom: 16, textAlign: 'center' }}>
                <IntlMessages id="onboarding.completion.title" />
              </Title>
              <Paragraph style={{ color: '#666', fontSize: 16, marginBottom: 32, textAlign: 'center' }}>
                <IntlMessages id="onboarding.completion.description" />
              </Paragraph>

              <Space direction="vertical" size="large" style={{ width: '100%', marginBottom: 40 }}>
                <div
                  className="completion-action-card"
                  onClick={() => {
                    if (authCompany) {
                      track('ONBOARDING_STEP_5_LINK_CLICKED', {
                        link_clicked: 'request_leave',
                        company_id: authCompany.id,
                      })
                    }
                    window.open('/app/leaves/request-leave')
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: 18 }}>📅</span>
                    <Text
                      style={{
                        fontSize: 16,
                        color: '#262626',
                        display: 'block',
                        visibility: 'visible',
                        opacity: 1,
                      }}
                    >
                      <>
                        <IntlMessages id="onboarding.completion.actions.seeHowItWorks.prefix" />{' '}
                        <span className="onboarding-inline-link" style={{ fontWeight: 'bold' }}>
                          <IntlMessages id="onboarding.completion.actions.seeHowItWorks.link" />
                        </span>{' '}
                        <IntlMessages id="onboarding.completion.actions.seeHowItWorks.suffix" />
                      </>
                    </Text>
                  </div>
                </div>

                <div
                  className="completion-action-card"
                  onClick={() => {
                    if (authCompany) {
                      track('ONBOARDING_STEP_5_LINK_CLICKED', {
                        link_clicked: 'leave_policies',
                        company_id: authCompany.id,
                      })
                    }
                    const locationId = data.authUser?.location?.id
                    if (locationId) {
                      window.open(`/app/settings/locations/${locationId}/leave-policies`)
                    } else {
                      window.open('/app/settings/locations')
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <span style={{ fontSize: 18, marginTop: 0, marginRight: 8, flexShrink: 0 }}>🔧</span>
                    <Text
                      style={{
                        fontSize: 16,
                        color: '#262626',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'left',
                      }}
                    >
                      <IntlMessages id="onboarding.completion.actions.customize" />{' '}
                      <span className="onboarding-inline-link" style={{ fontWeight: 'bold' }}>
                        <IntlMessages id="onboarding.completion.actions.leavePolicies" />
                      </span>
                    </Text>
                  </div>
                </div>

                <div
                  className="completion-action-card"
                  onClick={() => {
                    if (authCompany) {
                      track('ONBOARDING_STEP_5_LINK_CLICKED', {
                        link_clicked: 'team',
                        company_id: authCompany.id,
                      })
                    }
                    window.open('/app/users')
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <span style={{ fontSize: 18, marginTop: 0, marginRight: 8, flexShrink: 0 }}>👥</span>
                    <Text
                      style={{
                        fontSize: 16,
                        color: '#262626',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'left',
                      }}
                    >
                      <IntlMessages id="onboarding.completion.actions.importTeam" />{' '}
                      <span className="onboarding-inline-link" style={{ fontWeight: 'bold' }}>
                        <IntlMessages id="onboarding.completion.actions.team" />
                      </span>
                    </Text>
                  </div>
                </div>

                <div
                  className="completion-action-card"
                  onClick={() => {
                    if (authCompany) {
                      track('ONBOARDING_STEP_5_LINK_CLICKED', {
                        link_clicked: 'locations',
                        company_id: authCompany.id,
                      })
                    }
                    window.open('/app/settings/locations')
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <span style={{ fontSize: 18, marginTop: 0, marginRight: 8, flexShrink: 0 }}>🏢</span>
                    {authCompany?.plan === SubscriptionPlanEnum.freemium ? (
                      <Text
                        style={{
                          fontSize: 16,
                          color: '#262626',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                          minWidth: 0,
                          textAlign: 'left',
                        }}
                      >
                        <IntlMessages id="onboarding.completion.actions.viewAndManage" />{' '}
                        <span className="onboarding-inline-link" style={{ fontWeight: 'bold' }}>
                          <IntlMessages id="app.location" />
                        </span>{' '}
                        <IntlMessages id="onboarding.completion.actions.or" />{' '}
                        <span
                          className="onboarding-inline-link"
                          style={{ fontWeight: 'bold', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (authCompany) {
                              track('ONBOARDING_STEP_5_LINK_CLICKED', {
                                link_clicked: 'departments',
                                company_id: authCompany.id,
                              })
                            }
                            window.open('/app/settings/departments')
                          }}
                        >
                          <IntlMessages id="app.department" />
                        </span>
                      </Text>
                    ) : (
                      <Text
                        style={{
                          fontSize: 16,
                          color: '#262626',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                          minWidth: 0,
                          textAlign: 'left',
                        }}
                      >
                        <IntlMessages id="onboarding.completion.actions.addMore" />{' '}
                        <span className="onboarding-inline-link" style={{ fontWeight: 'bold' }}>
                          <IntlMessages id="app.locations" />
                        </span>{' '}
                        <IntlMessages id="onboarding.completion.actions.or" />{' '}
                        <span
                          className="onboarding-inline-link"
                          style={{ fontWeight: 'bold', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (authCompany) {
                              track('ONBOARDING_STEP_5_LINK_CLICKED', {
                                link_clicked: 'departments',
                                company_id: authCompany.id,
                              })
                            }
                            window.open('/app/settings/departments')
                          }}
                        >
                          <IntlMessages id="app.departments" />
                        </span>
                      </Text>
                    )}
                  </div>
                </div>

                <div
                  className="completion-action-card"
                  onClick={() => {
                    if (authCompany) {
                      track('ONBOARDING_STEP_5_LINK_CLICKED', {
                        link_clicked: 'reports',
                        company_id: authCompany.id,
                      })
                    }
                    window.open('/app/reports')
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <span style={{ fontSize: 18, marginTop: 0, marginRight: 8, flexShrink: 0 }}>📊</span>
                    <Text
                      style={{
                        fontSize: 16,
                        color: '#262626',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'left',
                      }}
                    >
                      <IntlMessages id="onboarding.completion.actions.view" />{' '}
                      <span className="onboarding-inline-link" style={{ fontWeight: 'bold' }}>
                        <IntlMessages id="onboarding.completion.actions.reports" />
                      </span>{' '}
                      <IntlMessages id="onboarding.completion.actions.or" /> <IntlMessages id="onboarding.completion.actions.setup" />{' '}
                      <span
                        className="onboarding-inline-link"
                        style={{ fontWeight: 'bold', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (authCompany) {
                            track('ONBOARDING_STEP_5_LINK_CLICKED', {
                              link_clicked: 'notifications',
                              company_id: authCompany.id,
                            })
                          }
                          window.open('/app/notifications')
                        }}
                      >
                        <IntlMessages id="onboarding.completion.actions.setupNotifications" />
                      </span>
                    </Text>
                  </div>
                </div>
              </Space>

              <div style={{ textAlign: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    if (authCompany && data?.authUser) {
                      track('ONBOARDING_STEP_5_CONTINUE_CLICKED', {
                        company_id: authCompany.id,
                        user_id: data.authUser.id,
                      })
                    }
                    onClose?.()
                    setTimeout(() => {
                      history.push('/app/users')
                    }, 50)
                  }}
                  loading={isLoading}
                  className="completion-dashboard-btn"
                >
                  <IntlMessages id="users.importNewUsers" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="step-right-section">
          <div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Location overview card */}
              <Card className="completion-summary-card">
                <Title level={4} style={{ fontSize: 18, marginBottom: 24, color: '#7F00FF' }}>
                  {locationName}
                </Title>

                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8, color: '#262626' }}>
                      <IntlMessages id="onboarding.completion.locationOverview.policies" />
                    </Text>
                    <Space size="small" wrap className={leavePolicies.length > 0 ? 'policies-list' : undefined}>
                      {leavePolicies.length > 0 ? (
                        leavePolicies.map((policy, index) => (
                          <Text key={index} type="secondary" style={{ fontSize: 14 }}>
                            {policy.leaveType?.name}
                          </Text>
                        ))
                      ) : (
                        <Text type="secondary" style={{ fontSize: 14 }}>
                          <IntlMessages id="onboarding.completion.locationOverview.noPolicies" />
                        </Text>
                      )}
                    </Space>
                  </div>

                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8, color: '#262626' }}>
                      <IntlMessages id="onboarding.completion.locationOverview.holidays" />
                    </Text>
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      <IntlMessages id="onboarding.completion.locationOverview.federalHolidays" values={{ count: holidaysCount }} />
                    </Text>
                  </div>
                </Space>
              </Card>

              {/* Department Management summary */}
              <Card className="completion-summary-card">
                <Title level={4} style={{ fontSize: 18, marginBottom: 24, color: '#7F00FF' }}>
                  <IntlMessages id="onboarding.completion.departmentManagement.title" />
                </Title>

                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8, color: '#262626' }}>
                      <IntlMessages id="onboarding.completion.departmentManagement.approvers" />
                    </Text>
                    <Space size="small">
                      {approvers.length > 0 ? (
                        <>
                          {approvers.slice(0, 4).map((approver) => (
                            <UserAvatar
                              key={approver.id}
                              id={approver.id}
                              name={approver.name || 'Approver'}
                              avatar={approver.imageUrl}
                              isShowingName={false}
                              isTooltip={true}
                              isLinkToPage={false}
                              shape="circle"
                              avatarSize="small"
                            />
                          ))}
                          {approvers.length > 4 && <span className="onb-inline-count">+{approvers.length - 4}</span>}
                        </>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 14 }}>
                          <IntlMessages id="onboarding.completion.departmentManagement.noApprovers" />
                        </Text>
                      )}
                    </Space>
                  </div>

                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8, color: '#262626' }}>
                      <IntlMessages id="onboarding.completion.departmentManagement.users" />
                    </Text>
                    <Space size="small">
                      {users.length > 0 ? (
                        users.slice(0, 3).map((user, index) => (
                          <Avatar src={user.imageUrl} key={index} size="small" style={{ backgroundColor: '#7F00FF' }}>
                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                          </Avatar>
                        ))
                      ) : (
                        <Text type="secondary" style={{ fontSize: 14 }}>
                          <IntlMessages id="onboarding.completion.departmentManagement.noUsers" />
                        </Text>
                      )}
                    </Space>
                  </div>
                </Space>
              </Card>
            </Space>
          </div>
        </div>
      </div>
    </div>
  )
}
